"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { Drop as DropType, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useMemo } from "react";
import DropContext from "./DropContext";
import type { DropIdentityMode, DropInteractionParams, DropLocation, DropTimestampLayout } from "./drop.types";
import ParticipationDrop from "./participation/ParticipationDrop";
import WaveDrop from "./WaveDrop";
import WinnerDrop from "./winner/WinnerDrop";
export type { DropInteractionParams } from "./drop.types";
export { DropLocation } from "./drop.types";
interface DropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: DropType | null;
  readonly nextDrop: DropType | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  readonly wrapContentOnly?: (content: React.ReactNode) => React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
export default function Drop(t0) {
  const $ = _c(63);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    parentContainerRef,
    wrapContentOnly,
    footer,
    identityMode,
    timestampLayout,
    showInteractions: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== activeDrop || $[1] !== drop || $[2] !== footer || $[3] !== identityMode || $[4] !== isVotingClosed || $[5] !== isVotingControlsLocked || $[6] !== location || $[7] !== onDropContentClick || $[8] !== onQuoteClick || $[9] !== onReply || $[10] !== parentContainerRef || $[11] !== showInteractions || $[12] !== showReplyAndQuote || $[13] !== showWaveInfo || $[14] !== timestampLayout || $[15] !== winningThreshold) {
    t4 = <ParticipationDrop drop={drop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} onReply={onReply} onQuoteClick={onQuoteClick} onDropContentClick={onDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[0] = activeDrop;
    $[1] = drop;
    $[2] = footer;
    $[3] = identityMode;
    $[4] = isVotingClosed;
    $[5] = isVotingControlsLocked;
    $[6] = location;
    $[7] = onDropContentClick;
    $[8] = onQuoteClick;
    $[9] = onReply;
    $[10] = parentContainerRef;
    $[11] = showInteractions;
    $[12] = showReplyAndQuote;
    $[13] = showWaveInfo;
    $[14] = timestampLayout;
    $[15] = winningThreshold;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== activeDrop || $[18] !== drop || $[19] !== dropViewDropId || $[20] !== footer || $[21] !== identityMode || $[22] !== location || $[23] !== nextDrop || $[24] !== onDropContentClick || $[25] !== onQuoteClick || $[26] !== onReply || $[27] !== onReplyClick || $[28] !== parentContainerRef || $[29] !== previousDrop || $[30] !== showInteractions || $[31] !== showReplyAndQuote || $[32] !== showWaveInfo || $[33] !== timestampLayout) {
    t5 = <WinnerDrop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={onDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} />;
    $[17] = activeDrop;
    $[18] = drop;
    $[19] = dropViewDropId;
    $[20] = footer;
    $[21] = identityMode;
    $[22] = location;
    $[23] = nextDrop;
    $[24] = onDropContentClick;
    $[25] = onQuoteClick;
    $[26] = onReply;
    $[27] = onReplyClick;
    $[28] = parentContainerRef;
    $[29] = previousDrop;
    $[30] = showInteractions;
    $[31] = showReplyAndQuote;
    $[32] = showWaveInfo;
    $[33] = timestampLayout;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  const t6 = previousDrop?.type === DropSize.FULL ? previousDrop : null;
  const t7 = nextDrop?.type === DropSize.FULL ? nextDrop : null;
  let t8;
  if ($[35] !== activeDrop || $[36] !== drop || $[37] !== dropViewDropId || $[38] !== footer || $[39] !== identityMode || $[40] !== location || $[41] !== onDropContentClick || $[42] !== onQuoteClick || $[43] !== onReply || $[44] !== onReplyClick || $[45] !== showInteractions || $[46] !== showReplyAndQuote || $[47] !== showWaveInfo || $[48] !== t6 || $[49] !== t7 || $[50] !== timestampLayout || $[51] !== wrapContentOnly) {
    t8 = <WaveDrop drop={drop} previousDrop={t6} nextDrop={t7} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={onDropContentClick} showReplyAndQuote={showReplyAndQuote} wrapContentOnly={wrapContentOnly} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} />;
    $[35] = activeDrop;
    $[36] = drop;
    $[37] = dropViewDropId;
    $[38] = footer;
    $[39] = identityMode;
    $[40] = location;
    $[41] = onDropContentClick;
    $[42] = onQuoteClick;
    $[43] = onReply;
    $[44] = onReplyClick;
    $[45] = showInteractions;
    $[46] = showReplyAndQuote;
    $[47] = showWaveInfo;
    $[48] = t6;
    $[49] = t7;
    $[50] = timestampLayout;
    $[51] = wrapContentOnly;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  let t9;
  if ($[53] !== t4 || $[54] !== t5 || $[55] !== t8) {
    t9 = {
      [ApiDropType.Participatory]: t4,
      [ApiDropType.Winner]: t5,
      [ApiDropType.Chat]: t8
    };
    $[53] = t4;
    $[54] = t5;
    $[55] = t8;
    $[56] = t9;
  } else {
    t9 = $[56];
  }
  const components = t9;
  let t10;
  if ($[57] !== drop || $[58] !== location) {
    t10 = {
      drop,
      location
    };
    $[57] = drop;
    $[58] = location;
    $[59] = t10;
  } else {
    t10 = $[59];
  }
  const memoizedValue = t10;
  const t11 = components[drop.drop_type];
  let t12;
  if ($[60] !== memoizedValue || $[61] !== t11) {
    t12 = <DropContext.Provider value={memoizedValue}>{t11}</DropContext.Provider>;
    $[60] = memoizedValue;
    $[61] = t11;
    $[62] = t12;
  } else {
    t12 = $[62];
  }
  return t12;
}